import React, { ReactElement } from 'react';
import styled from 'styled-components';
import { colorsPalette } from '../../styles/colors-palette';

interface FormItemCustomStyles {
  marginBottom?: CSSSize;
  alignItems?: React.CSSProperties['alignItems'];
}

interface LabeledFormItemBaseProps {
  label?: React.ReactNode;
  children: React.ReactNode;
  direction?: LabeledFormItemLayoutDirection;
  className?: string;
  labelMarginTop?: CSSSize;
  customStyles?: FormItemCustomStyles;
}

interface ILayoutTemplateProps {
  label?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  contentWidth?: CSSSize;
  labelWidth?: CSSSize;
  labelMinWidth?: CSSSize;
  labelAlignSelf?: LabelAlignSelf;
  labelMarginTop?: CSSSize;
  customStyles?: FormItemCustomStyles;
}

interface LabeledFormItemPropsHorizontal extends LabeledFormItemBaseProps {
  direction?: 'horizontal';
  labelWidth?: CSSSize;
  labelMinWidth?: CSSSize;
  labelAlignSelf?: LabelAlignSelf;
}

interface LabeledFormItemPropsVertical extends LabeledFormItemBaseProps {
  direction?: 'vertical';
  labelWidth?: CSSSize;
  labelMinWidth?: CSSSize;
  labelAlignSelf?: LabelAlignSelf;
}

type LabeledFormItemProps = LabeledFormItemPropsHorizontal | LabeledFormItemPropsVertical;
type LabeledFormItemLayoutDirection = 'horizontal' | 'vertical';
type LabelAlignSelf = 'flex-start';

function FormItem({
  label,
  direction = 'horizontal',
  className,
  labelWidth = '220px',
  labelAlignSelf,
  labelMarginTop,
  labelMinWidth,
  customStyles,
  children,
}: LabeledFormItemProps): ReactElement {
  const DirectionWrapper = direction === 'horizontal' ? HorizontalWrapper : VerticalWrapper;

  return (
    <DirectionWrapper className={className} customStyles={customStyles}>
      {(direction !== 'vertical' || !!label) && (
        <Label
          marginTop={labelMarginTop}
          labelAlignSelf={labelAlignSelf}
          minWidth={labelMinWidth}
          width={labelWidth}
        >
          {label}
        </Label>
      )}
      <Content>{children}</Content>
    </DirectionWrapper>
  );
}

function HorizontalLayoutTemplate({
  label,
  labelWidth,
  labelAlignSelf,
  labelMarginTop,
  labelMinWidth,
  children,
  className,
  contentWidth = '300px',
  customStyles,
}: ILayoutTemplateProps) {
  return (
    <FormItem
      direction={'horizontal'}
      label={label}
      labelMinWidth={labelMinWidth}
      labelAlignSelf={labelAlignSelf}
      labelMarginTop={labelMarginTop}
      labelWidth={labelWidth}
      customStyles={customStyles}
      className={className}
    >
      <HorizontalLayoutContentWrapper>
        <HorizontalLayoutContent contentWidth={contentWidth}>{children}</HorizontalLayoutContent>
      </HorizontalLayoutContentWrapper>
    </FormItem>
  );
}

function VerticalLayoutTemplate({
  label,
  children,
  className,
  customStyles,
}: ILayoutTemplateProps) {
  return (
    <FormItem
      className={className}
      direction={'vertical'}
      label={label}
      labelWidth={'100%'}
      customStyles={customStyles}
    >
      <VerticalContentWrapper>{children}</VerticalContentWrapper>
    </FormItem>
  );
}

const HorizontalWrapper = styled.div<{ customStyles?: FormItemCustomStyles }>`
  display: flex;
  flex-direction: row;
  align-items: ${props =>
    props.customStyles?.alignItems ? props.customStyles?.alignItems : 'center'};
  margin-bottom: ${props => props.customStyles?.marginBottom ?? '32px'};
`;

const HorizontalLayoutContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const HorizontalLayoutContent = styled.div<{ contentWidth?: CSSSize }>`
  width: ${props => props.contentWidth};
`;

const VerticalWrapper = styled.div<{ customStyles?: FormItemCustomStyles }>`
  display: flex;
  flex-direction: column;
  margin-bottom: ${props => props.customStyles?.marginBottom};
`;

const VerticalContentWrapper = styled.div`
  width: 100%;
  margin-top: 8px;
`;

const Label = styled.label<{
  marginTop?: CSSSize;
  width?: CSSSize;
  minWidth?: CSSSize;
  labelAlignSelf?: LabelAlignSelf;
}>`
  font-size: 14px;
  height: 21px;
  color: ${colorsPalette.gray600};
  margin-top: ${props => props.marginTop};
  width: ${props => props.width};
  min-width: ${props => props.minWidth};
  text-overflow: ellipsis;
  align-self: ${props => props.labelAlignSelf};
`;

const Content = styled.div`
  display: flex;
  width: 100%;
`;

FormItem.HorizontalLayoutTemplate = HorizontalLayoutTemplate;
FormItem.VerticalLayoutTemplate = VerticalLayoutTemplate;

export { FormItem };
